import React, { useRef, useState } from "react";
import { Box, Typography, Button, LinearProgress, IconButton, Chip, Alert } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { uploadAppServiceIconApi } from "../../../redux/api/api";

const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

// Keep in sync with Api/utils/r2Storage.js SERVICE_ICON_SPEC / MAX_SERVICE_ICON_SIZE_BYTES
// (also returned by /api/admin/app-service/meta as `iconSpec`).
const MAX_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB
const RECOMMENDED_PX = 512;
const MIN_PX = 256;

const formatBytes = (bytes) => {
  if (!bytes) return "";
  const kb = bytes / 1024;
  return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.round(kb)} KB`;
};

/**
 * R2-backed service-icon uploader. Hard rules (enforced again server-side in
 * Api/controllers/Admin/appService.controller.js): JPG/PNG/WebP, max 2 MB.
 * Dimensions are advisory — a non-square or small image still uploads but
 * shows a warning, since the admin may have a reason.
 *
 * On success calls onUploaded({ objectKey, imageUrl, fileSize, mimeType, width, height }).
 */
const ServiceIconUploader = ({ serviceId, value, onUploaded, onRemove }) => {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");

  const readImageDimensions = (file) =>
    new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
        URL.revokeObjectURL(url);
      };
      img.onerror = () => {
        resolve({ width: null, height: null });
        URL.revokeObjectURL(url);
      };
      img.src = url;
    });

  const dimensionWarning = ({ width, height }) => {
    if (!width || !height) return "";
    const ratio = width / height;
    if (ratio < 0.9 || ratio > 1.11) {
      return `This image is ${width}×${height}px (not square). It will be cropped/letterboxed to fit the square tile — a 1:1 image looks best.`;
    }
    if (width < MIN_PX || height < MIN_PX) {
      return `This image is only ${width}×${height}px — it may look soft on high-resolution phones. Aim for ${RECOMMENDED_PX}×${RECOMMENDED_PX}px.`;
    }
    return "";
  };

  const handleFile = async (file) => {
    setError("");
    setWarning("");
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) return setError("Icon must be a PNG, WebP or JPG file.");
    if (file.size > MAX_SIZE_BYTES) {
      return setError(`This file is ${formatBytes(file.size)} — the maximum is 2 MB. Compress or resize it and try again.`);
    }
    if (!serviceId) return setError("Pick an Internal Service first, then upload the icon.");

    const dimensions = await readImageDimensions(file);
    const dimWarn = dimensionWarning(dimensions);

    const formData = new FormData();
    formData.append("icon", file);
    formData.append("serviceId", serviceId);

    setUploading(true);
    setProgress(0);
    try {
      const response = await uploadAppServiceIconApi(formData, setProgress);
      const info = response?.data?.info || response?.data?.data;
      onUploaded({ ...info, width: dimensions.width, height: dimensions.height });
      if (dimWarn) setWarning(dimWarn);
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to upload icon. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    handleFile(event.dataTransfer?.files?.[0]);
  };

  const specLine = `PNG / WebP / JPG · ${RECOMMENDED_PX} × ${RECOMMENDED_PX} px recommended (1:1 square) · Max 2 MB`;

  if (value?.imageUrl) {
    return (
      <Box>
        <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 2, p: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              component="img"
              src={value.imageUrl}
              alt="Service icon"
              sx={{ width: 64, height: 64, objectFit: "cover", borderRadius: 2, border: "1px solid #eee" }}
            />
            <Box sx={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {value.width && value.height ? <Chip size="small" label={`${value.width} × ${value.height}px`} /> : null}
              {value.fileSize ? <Chip size="small" label={formatBytes(value.fileSize)} /> : null}
            </Box>
            <Button size="small" startIcon={<RefreshIcon />} onClick={() => inputRef.current?.click()}>
              Replace
            </Button>
            <IconButton size="small" color="error" onClick={onRemove}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <input ref={inputRef} type="file" accept={ALLOWED_TYPES.join(",")} hidden onChange={(e) => handleFile(e.target.files?.[0])} />
        </Box>
        {warning ? (
          <Alert severity="warning" sx={{ mt: 1, py: 0.25 }}>
            {warning}
          </Alert>
        ) : null}
        {error ? (
          <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
            {error}
          </Typography>
        ) : null}
      </Box>
    );
  }

  return (
    <Box>
      <Box
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        sx={{
          border: "2px dashed",
          borderColor: dragActive ? "primary.main" : "#c9c9d1",
          backgroundColor: dragActive ? "rgba(25,118,210,0.04)" : "#fafafa",
          borderRadius: 2,
          p: 2.5,
          textAlign: "center",
          cursor: uploading ? "default" : "pointer",
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 28, color: "text.secondary", mb: 0.5 }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Upload icon
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {specLine}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Crop tight so the subject fills the frame. Use a transparent PNG for logos.
        </Typography>
        {uploading ? (
          <Box sx={{ mt: 1.5 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" color="text.secondary">
              Uploading… {progress}%
            </Typography>
          </Box>
        ) : null}
        <input ref={inputRef} type="file" accept={ALLOWED_TYPES.join(",")} hidden onChange={(e) => handleFile(e.target.files?.[0])} />
      </Box>
      {error ? (
        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
};

export default ServiceIconUploader;
